/**
 * Lay Deck — force navigation transitions to "none" whenever a deck slide is
 * involved (source or destination of the nav, OR the very first page load).
 *
 * Two override points:
 *
 *   1. **Hard-load of a deck slide.** On init, if the current page is a deck
 *      slide we disable `navigation_transition_use_revealing_transition_on_first_visit`
 *      so the first-visit "Showing Animation" doesn't play. We also set
 *      `navigation_transition_in` to 'none' for symmetry with point 2.
 *
 *   2. **AJAX nav involving a deck slide.** A click-capture listener mutates
 *      layData's `navigation_transition_*` settings to 'none'/0 before
 *      history_controller reads them. Triggers when current body is a deck
 *      slide (deck → anywhere) OR the target anchor is a deck-slide link
 *      (anywhere → deck). After `newpageshown`, the original values are
 *      restored so non-deck-related navs keep the user's configured Navigation
 *      Animations intact.
 *
 * Why click capture: bind_anchors.js's delegate runs on the click bubble phase
 * and immediately calls history_controller.pushState(), which then reads
 * layData. We need to mutate layData FIRST. Adding a listener with
 * `{ capture: true }` runs in the capture phase before any jQuery delegate
 * bound on document — covers real clicks AND synthetic .click() calls (e.g.
 * the deck counter's keyboard shortcut handler).
 */

var disable_transitions_for_deck = {
	i: function () {
		init()
	},
}

var stash = null

function bodyIsDeck() {
	if (!document.body) return false
	return document.body.getAttribute('data-type') === 'lay_deck'
}

function findAnchor(target) {
	if (!target) return null
	if (typeof target.closest === 'function') return target.closest('a[data-type]')
	// Walk up manually for very old browsers.
	var el = target
	while (el && el.nodeType === 1) {
		if (el.tagName === 'A' && el.getAttribute && el.getAttribute('data-type')) return el
		el = el.parentNode
	}
	return null
}

function shouldDisableTransitions(targetAnchor) {
	// Disable whenever EITHER side of the navigation is a deck slide so the
	// deck experience never inherits the site-wide Navigation Animations.
	var targetIsDeck = !!(targetAnchor && targetAnchor.getAttribute('data-type') === 'lay_deck')
	return targetIsDeck || bodyIsDeck()
}

function applyNoneTransitions() {
	if (typeof layData === 'undefined' || layData === null) return
	if (stash) return // already overridden for this nav
	stash = {
		out:     layData.navigation_transition_out,
		in:      layData.navigation_transition_in,
		out_dur: layData.navigation_transition_out_duration,
		in_dur:  layData.navigation_transition_in_duration,
	}
	layData.navigation_transition_out = 'none'
	layData.navigation_transition_in = 'none'
	layData.navigation_transition_out_duration = 0
	layData.navigation_transition_in_duration = 0
}

function restoreTransitions() {
	if (!stash) return
	if (typeof layData !== 'undefined' && layData !== null) {
		layData.navigation_transition_out = stash.out
		layData.navigation_transition_in = stash.in
		layData.navigation_transition_out_duration = stash.out_dur
		layData.navigation_transition_in_duration = stash.in_dur
	}
	stash = null
}

function onCaptureClick(e) {
	var anchor = findAnchor(e.target)
	if (!shouldDisableTransitions(anchor)) return
	applyNoneTransitions()
}

function init() {
	// First-load case: if the very first page in this session is a deck slide,
	// disable the showing-on-first-visit reveal so the "Showing Animation"
	// doesn't play. animations_controller reads this flag once on isFirstLoad
	// and never again, so we don't need to restore it later. We don't touch
	// navigation_transition_in here — the click capture below handles all
	// in-page-life navigations involving a deck slide.
	if (typeof layData !== 'undefined' && layData !== null && bodyIsDeck()) {
		layData.navigation_transition_use_revealing_transition_on_first_visit = ''
	}

	document.addEventListener('click', onCaptureClick, true)
	if (window.laytheme && typeof window.laytheme.on === 'function') {
		// `transition_navigation_in_completed` fires AFTER doTransitionIn has
		// run — restoring on `newpageshown` would race the transition_in handler
		// (newpageshown fires first, then transition_in via setTimeout(0)) and
		// the animator would read the already-restored 'fade' values, defeating
		// the override entirely.
		window.laytheme.on('transition_navigation_in_completed', restoreTransitions)
	}
}

export default disable_transitions_for_deck
